import { applyOpacity } from "../../utils/colors";
import { Palette } from "./Palette";

type SurfacesStateProps = {
	rest: string;
	hover: string;
	selected: string;
	selectedHover: string;
};

type ButtonStateProps = {
	rest: string;
	hover: string;
	disabled: string;
};

export type ColorTokensProps = {
	Background: SurfacesStateProps & { hoverHover: string };
	Layer: {
		O1: SurfacesStateProps;
		O2: SurfacesStateProps;
		O3: SurfacesStateProps;
		selectedInverse: string;
		selectedDisabled: string;
	};
	Border: {
		rest: string;
		selected: string;
		error: string;
		disabled: string;
	};
	Text: {
		primary: string;
		secondary: string;
		helper: string;
		disabled: string;
		error: string;
		inverse: string;
		onColor: string;
		onColorDisabled: string;
		onColorInverse: string;
	};
	Icon: {
		primary: string;
		disabled: string;
		error: string;
		selected: string;
		selectedHover: string;
		inverse: string;
		onColor: string;
		onColorDisabled: string;
		onColorInverse: string;
	};
	Button: {
		primary: ButtonStateProps;
		secondary: ButtonStateProps;
		tertiary: ButtonStateProps;
	};
	Support: {
		error: string;
		success: string;
		successText: string;
		attention: string;
		info: string;
	};
	Miscellaneous: {
		control: SurfacesStateProps & { restDisabled: string; selectedDisabled: string };
		overlay: string;
		field: string;
		premium: string;
		beta: string;
		divider: string;
		scrollbar: string;
	};
	Link: {
		rest: string;
		hover: string;
		visited: string;
		disabled: string;
	};
	Avatar: AvatarColorTokens;
	Dropzone: {
		sectionColor: string;
		border: string;
		background: string;
	};
	Tag: TagColorTokens;
};

export type ElevationTokenProps = {
	One: string;
	Two: string;
	Three: string;
};

export const ElevationLightTheme = {
	One: "0px 2px 3px rgba(0, 0, 0, 0.18)",
	Two: "0px 1px 6px rgba(0, 0, 0, 0.06), 0px 6px 12px rgba(0, 0, 0, 0.12)",
	Three:
		"0px 8px 17px rgba(0, 0, 0, 0.1), 0px 1px 6px rgba(0, 0, 0, 0.06), 0px 6px 12px rgba(0, 0, 0, 0.06)",
};

export const ElevationDarkTheme = {
	One: "0px 2px 3px rgba(0, 0, 0, 0.18)",
	Two: "0px 1px 6px rgba(0, 0, 0, 0.06), 0px 6px 12px rgba(0, 0, 0, 0.12)",
	Three:
		"0px 8px 17px rgba(0, 0, 0, 0.1), 0px 1px 6px rgba(0, 0, 0, 0.06), 0px 6px 12px rgba(0, 0, 0, 0.06)",
};

type AvatarColorTokens = {
	AvatarRed: string;
	AvatarOrange: string;
	AvatarPurple: string;
	AvatarBlue: string;
	AvatarGreen: string;
	AvatarTeal: string;
	AvatarGold: string;
	AvatarPink: string;
	AvatarDarkGray: string;
	AvatarLightGray: string;
	AvatarMint: string;
	AvatarHoney: string;
};

type TagColorTokens = {
	TagPurple: string;
	TagGreen: string;
	TagOrange: string;
	TagTeal: string;
};

export const ColorTokensLightTheme: ColorTokensProps = {
	Background: {
		rest: Palette.Neutral.N10,
		hover: Palette.Neutral.N30,
		hoverHover: Palette.Neutral.N40,
		selected: applyOpacity(Palette.Brand.Brand20, 15),
		selectedHover: applyOpacity(Palette.Brand.Brand20, 27),
	},
	Layer: {
		O1: {
			rest: Palette.Neutral.N10,
			hover: Palette.Neutral.N30,
			selected: applyOpacity(Palette.Brand.Brand20, 5),
			selectedHover: applyOpacity(Palette.Brand.Brand20, 27),
		},
		O2: {
			rest: Palette.Neutral.N10,
			hover: Palette.Neutral.N30,
			selected: applyOpacity(Palette.Brand.Brand20, 5),
			selectedHover: applyOpacity(Palette.Brand.Brand20, 27),
		},
		O3: {
			rest: Palette.Neutral.N10,
			hover: Palette.Neutral.N30,
			selected: applyOpacity(Palette.Brand.Brand20, 5),
			selectedHover: applyOpacity(Palette.Brand.Brand20, 27),
		},
		selectedInverse: Palette.Neutral.N90,
		selectedDisabled: Palette.Neutral.N80,
	},
	Border: {
		rest: Palette.Neutral.N50,
		selected: Palette.Brand.Brand20,
		error: Palette.Red.Red20,
		disabled: Palette.Neutral.N30,
	},
	Text: {
		primary: Palette.Neutral.N150,
		secondary: Palette.Neutral.N100,
		helper: Palette.Neutral.N90,
		disabled: Palette.Neutral.N90,
		onColor: Palette.Neutral.N10,
		onColorInverse: Palette.Neutral.N150,
		onColorDisabled: applyOpacity(Palette.Neutral.N10, 65),
		error: Palette.Red.Red20,
		inverse: Palette.Neutral.N10,
	},
	Icon: {
		primary: Palette.Neutral.N150,
		disabled: Palette.Neutral.N65,
		error: Palette.Red.Red20,
		selected: Palette.Brand.Brand20,
		selectedHover: Palette.Brand.Brand30,
		inverse: Palette.Neutral.N10,
		onColor: Palette.Neutral.N10,
		onColorDisabled: applyOpacity(Palette.Neutral.N10, 65),
		onColorInverse: Palette.Neutral.N150,
	},
	Button: {
		primary: {
			rest: Palette.Brand.Brand20,
			hover: Palette.Brand.Brand30,
			disabled: Palette.Brand.Brand10,
		},
		secondary: {
			rest: Palette.Neutral.N30,
			hover: Palette.Neutral.N40,
			disabled: Palette.Neutral.N30,
		},
		tertiary: {
			rest: Palette.Brand.Brand20,
			hover: Palette.Brand.Brand20,
			disabled: Palette.Brand.Brand10,
		},
	},
	Support: {
		error: Palette.Red.Red20,
		success: Palette.Green.Green10,
		successText: Palette.Green.Green10,
		attention: Palette.Orange.Orange10,
		info: Palette.Neutral.N100,
	},
	Miscellaneous: {
		control: {
			rest: Palette.Neutral.N40,
			hover: Palette.Neutral.N50,
			selected: Palette.Brand.Brand20,
			selectedHover: Palette.Brand.Brand30,
			restDisabled: Palette.Neutral.N40,
			selectedDisabled: Palette.Brand.Brand10,
		},
		overlay: applyOpacity(Palette.Neutral.N150, 50),
		field: Palette.Neutral.N20,
		premium: Palette.Yellow.Yellow10,
		beta: Palette.Purple.Purple30,
		divider: Palette.Neutral.N40,
		scrollbar: Palette.Neutral.N60,
	},
	Link: {
		rest: Palette.Brand.Brand20,
		hover: Palette.Brand.Brand20,
		visited: Palette.Purple.Purple20,
		disabled: Palette.Neutral.N90,
	},
	Avatar: {
		AvatarRed: Palette.Avatar.Red,
		AvatarOrange: Palette.Avatar.Orange,
		AvatarPurple: Palette.Avatar.Purple,
		AvatarBlue: Palette.Avatar.Blue,
		AvatarGreen: Palette.Avatar.Green,
		AvatarTeal: Palette.Avatar.Teal,
		AvatarGold: Palette.Avatar.Gold,
		AvatarPink: Palette.Avatar.Pink,
		AvatarDarkGray: Palette.Avatar.DarkGrey,
		AvatarLightGray: Palette.Avatar.LightGrey,
		AvatarMint: Palette.Avatar.Mint,
		AvatarHoney: Palette.Avatar.Honey,
	},
	Dropzone: {
		sectionColor: Palette.Neutral.N20,
		border: Palette.Brand.Brand20,
		background: applyOpacity(Palette.Brand.Brand20, 15),
	},
	Tag: {
		TagPurple: applyOpacity(Palette.Tag.Purple, 10),
		TagGreen: applyOpacity(Palette.Tag.Mint, 10),
		TagOrange: applyOpacity(Palette.Tag.Orange, 10),
		TagTeal: applyOpacity(Palette.Tag.Teal, 10),
	},
};

export const ColorTokensDarkTheme: ColorTokensProps = {
	Background: {
		rest: Palette.Neutral.N150,
		hover: applyOpacity(Palette.Neutral.N10, 8),
		hoverHover: "", // TODO: To be defined
		selected: applyOpacity(Palette.Neutral.N10, 12),
		selectedHover: applyOpacity(Palette.Neutral.N10, 16),
	},
	Layer: {
		O1: {
			rest: Palette.Neutral.N140,
			hover: Palette.Neutral.N130,
			selected: Palette.Neutral.N120,
			selectedHover: Palette.Neutral.N110,
		},
		O2: {
			rest: Palette.Neutral.N120,
			hover: Palette.Neutral.N110,
			selected: Palette.Neutral.N100,
			selectedHover: Palette.Neutral.N90,
		},
		O3: {
			rest: Palette.Neutral.N100,
			hover: Palette.Neutral.N90,
			selected: Palette.Neutral.N80,
			selectedHover: Palette.Neutral.N70,
		},
		selectedInverse: Palette.Neutral.N30,
		selectedDisabled: Palette.Neutral.N80,
	},
	Border: {
		rest: Palette.Neutral.N80,
		selected: Palette.Brand.Brand20,
		error: Palette.Red.Red10,
		disabled: Palette.Neutral.N110,
	},
	Text: {
		primary: Palette.Neutral.N10,
		secondary: Palette.Neutral.N50,
		helper: Palette.Neutral.N70,
		disabled: Palette.Neutral.N70,
		onColor: Palette.Neutral.N10,
		onColorInverse: Palette.Neutral.N150,
		onColorDisabled: Palette.Neutral.N80,
		error: Palette.Red.Red10,
		inverse: Palette.Neutral.N150,
	},
	Icon: {
		primary: Palette.Neutral.N10,
		disabled: Palette.Neutral.N80,
		error: Palette.Red.Red10,
		selected: Palette.Brand.Brand20,
		selectedHover: Palette.Brand.Brand30,
		inverse: Palette.Neutral.N110,
		onColor: Palette.Neutral.N10,
		onColorDisabled: Palette.Neutral.N80,
		onColorInverse: Palette.Neutral.N150,
	},
	Button: {
		primary: {
			rest: Palette.Brand.Brand20,
			hover: Palette.Brand.Brand30,
			disabled: Palette.Brand.Brand40,
		},
		secondary: {
			rest: applyOpacity(Palette.Neutral.N10, 10),
			hover: applyOpacity(Palette.Neutral.N10, 22),
			disabled: applyOpacity(Palette.Neutral.N10, 6),
		},
		tertiary: {
			rest: Palette.Brand.Brand10,
			hover: Palette.Brand.Brand10,
			disabled: applyOpacity(Palette.Brand.Brand10, 50),
		},
	},
	Support: {
		error: Palette.Red.Red10,
		success: Palette.Green.Green10,
		successText: Palette.Green.Green20,
		attention: Palette.Orange.Orange10,
		info: Palette.Neutral.N50,
	},
	Miscellaneous: {
		control: {
			rest: Palette.Neutral.N110,
			hover: applyOpacity(Palette.Neutral.N10, 10),
			selected: Palette.Brand.Brand20,
			selectedHover: Palette.Brand.Brand30,
			restDisabled: Palette.Neutral.N120,
			selectedDisabled: Palette.Brand.Brand40,
		},
		overlay: applyOpacity(Palette.Neutral.N140, 80),
		field: applyOpacity(Palette.Neutral.N150, 42),
		premium: Palette.Yellow.Yellow10,
		beta: Palette.Purple.Purple30,
		divider: applyOpacity(Palette.Neutral.N10, 10),
		scrollbar: applyOpacity(Palette.Neutral.N10, 25),
	},
	Link: {
		rest: Palette.Brand.Brand10,
		hover: Palette.Brand.Brand10,
		visited: Palette.Purple.Purple10,
		disabled: Palette.Neutral.N70,
	},
	Avatar: {
		AvatarRed: Palette.Avatar.Red,
		AvatarOrange: Palette.Avatar.Orange,
		AvatarPurple: Palette.Avatar.Purple,
		AvatarBlue: Palette.Avatar.Blue,
		AvatarGreen: Palette.Avatar.Green,
		AvatarTeal: Palette.Avatar.Teal,
		AvatarGold: Palette.Avatar.Gold,
		AvatarPink: Palette.Avatar.Pink,
		AvatarDarkGray: Palette.Avatar.DarkGrey,
		AvatarLightGray: Palette.Avatar.LightGrey,
		AvatarMint: Palette.Avatar.Mint,
		AvatarHoney: Palette.Avatar.Honey,
	},
	Dropzone: {
		sectionColor: Palette.Neutral.N150,
		border: Palette.Brand.Brand20,
		background: applyOpacity(Palette.Brand.Brand20, 15),
	},
	Tag: {
		TagPurple: Palette.Tag.Purple,
		TagGreen: Palette.Tag.Mint,
		TagOrange: Palette.Tag.Orange,
		TagTeal: Palette.Tag.Teal,
	},
};
